import { ReactNode, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import type {
	CatalogLinkBody,
	CatalogProvider,
	CatalogProviderChangeResponse,
	CatalogRemoveBody,
	CatalogRow,
} from 'src/motornet/types';
import { formatCurrency, formatDate } from 'src/utils';
import SpinnerButton from 'src/components/SpinnerButton/SpinnerButton';
import toaster from 'src/utils/toaster';
import { useCatalogCandidates, useLinkProvider, useMarkAvailable, useMarkUnavailable, useRemoveProvider } from './api';
import {
	activeManualLink,
	chipState,
	currentRowWarning,
	facetMatch,
	findOption,
	isManualLink,
	matchesListPrice,
	matchesManufacturerCode,
	PROVIDER_LABELS,
	providerListings,
	providerView,
	rowLabel,
	selectedOption,
	versionManufacturerCodes,
} from './helpers';
import { DetailGrid, StatusNote } from './DetailGrid';
import { formatWithSubtitle, Option, portalProps, useSelectLabels } from './selectStyles';
import styles from './VehicleAssociation.module.scss';
import { WizardField, WizardFields } from './WizardFields';

type Change = { kind: 'link'; body: CatalogLinkBody } | { kind: 'remove'; body: CatalogRemoveBody };

type Props = {
	row: CatalogRow;
	provider: CatalogProvider;
	onClose: () => void;
};

const toOptions = (values: string[]): Option<string>[] => values.map((value) => ({ value, label: value }));

const ProviderPanel = ({ row, provider, onClose }: Props) => {
	const { t } = useTranslation();
	const state = chipState(row, provider);
	const [replacing, setReplacing] = useState(false);
	const [pending, setPending] = useState<Change | null>(null);
	const link = useLinkProvider();
	const remove = useRemoveProvider();
	const markUnavailable = useMarkUnavailable();
	const markAvailable = useMarkAvailable();
	const busy = link.isPending || remove.isPending;
	const marking = markUnavailable.isPending || markAvailable.isPending;
	const view = providerView(state, replacing, pending != null);
	const pendingText = t('vehicle_association_pending', {
		defaultValue: 'Modifica salvata, codici Motornet non ancora aggiornati: riprova',
	});

	const handleResult = (res: CatalogProviderChangeResponse, change: Change) => {
		if (res.vehiclesPending) {
			toaster.warning(pendingText);
			setPending(change);
			return;
		}
		setPending(null);
		const message =
			change.kind === 'link'
				? t('vehicle_association_saved', { defaultValue: 'Associazione salvata' })
				: t('vehicle_association_removed', { defaultValue: 'Noleggiatore rimosso' });
		toaster.success(
			res.movedFrom.length
				? `${message}. ${t('vehicle_association_moved_from', {
						labels: res.movedFrom.map((m) => m.label).join(', '),
						defaultValue: 'Tolta da: {{labels}}',
				  })}`
				: message
		);
		onClose();
	};

	const handleError = (err: Error) =>
		toaster.error(
			err.message ||
				t('vehicle_association_save_error', { defaultValue: "Errore nel salvataggio dell'associazione" })
		);

	const setAvailability = (available: boolean) => {
		const body = { versionKey: row.versionKey, provider };
		const onSuccess = () => {
			toaster.success(
				available
					? t('vehicle_association_marked_available', { defaultValue: 'Noleggiatore di nuovo disponibile' })
					: t('vehicle_association_marked_unavailable', { defaultValue: 'Noleggiatore segnato come non disponibile' })
			);
			onClose();
		};
		if (available) markAvailable.mutate(body, { onSuccess, onError: handleError });
		else markUnavailable.mutate(body, { onSuccess, onError: handleError });
	};

	const submit = (change: Change) => {
		const callbacks = {
			onSuccess: (res: CatalogProviderChangeResponse) => handleResult(res, change),
			onError: handleError,
		};
		if (change.kind === 'link') link.mutate(change.body, callbacks);
		else remove.mutate(change.body, callbacks);
	};

	return (
		<div className="p-3">
			{view === 'pending' && pending ? (
				<Alert variant="warning" className="d-flex align-items-center justify-content-between gap-3 mb-0">
					<span>{pendingText}</span>
					<div className="d-flex gap-2">
						<Button size="sm" variant="link" disabled={busy} onClick={onClose}>
							{t('close', { defaultValue: 'Chiudi' })}
						</Button>
						<Button size="sm" variant="warning" disabled={busy} onClick={() => submit(pending)}>
							<SpinnerButton
								busy={busy}
								label={t('vehicle_association_retry', { defaultValue: 'Riprova' })}
							/>
						</Button>
					</div>
				</Alert>
			) : view === 'wizard' ? (
				<ProviderWizard
					row={row}
					provider={provider}
					saving={link.isPending}
					disabled={marking}
					extraAction={
						state === 'missing' ? (
							<Button variant="outline-dark" disabled={link.isPending || marking} onClick={() => setAvailability(false)}>
								<SpinnerButton
									busy={markUnavailable.isPending}
									label={t('vehicle_association_mark_unavailable', { defaultValue: 'Non disponibile' })}
								/>
							</Button>
						) : state === 'unavailable' ? (
							<Button variant="outline-dark" disabled={link.isPending || marking} onClick={() => setAvailability(true)}>
								<SpinnerButton
									busy={markAvailable.isPending}
									label={t('vehicle_association_mark_available', { defaultValue: 'Disponibile' })}
								/>
							</Button>
						) : null
					}
					onCancel={state === 'manual' ? () => setReplacing(false) : onClose}
					onSave={(ratebookId) =>
						submit({ kind: 'link', body: { versionKey: row.versionKey, provider, ratebookId } })
					}
				/>
			) : (
				<ProviderSummary
					row={row}
					provider={provider}
					removing={remove.isPending}
					onClose={onClose}
					onReplace={() => setReplacing(true)}
					onRemove={() => submit({ kind: 'remove', body: { versionKey: row.versionKey, provider } })}
				/>
			)}
		</div>
	);
};

type SummaryProps = {
	row: CatalogRow;
	provider: CatalogProvider;
	removing: boolean;
	onClose: () => void;
	onReplace: () => void;
	onRemove: () => void;
};

const ProviderSummary = ({ row, provider, removing, onClose, onReplace, onRemove }: SummaryProps) => {
	const { t, i18n } = useTranslation();
	const [confirming, setConfirming] = useState(false);
	const detail = providerListings(row).find((p) => p.provider === provider);
	const manual = isManualLink(row, provider);
	const link = activeManualLink(row, provider);
	const name = PROVIDER_LABELS[provider];

	return (
		<>
			<div className={styles.sectionTitle}>{name}</div>
			{detail && (
				<DetailGrid
					items={[
						{ label: t('version', { defaultValue: 'Versione' }), value: detail.version ?? '-' },
						{
							label: t('vehicle_association_manufacturer_code', { defaultValue: 'Cod. costr.' }),
							value: detail.manufacturerCode ?? '-',
						},
						{
							label: t('vehicle_association_list_price', { defaultValue: 'Listino netto' }),
							value: detail.listPrice == null ? '-' : formatCurrency(detail.listPrice),
						},
						{ label: t('year', { defaultValue: 'Anno' }), value: detail.modelYear ?? '-' },
					]}
				/>
			)}
			<StatusNote kind={manual ? 'manual' : 'automatic'}>
				{manual
					? link
						? t('vehicle_association_manual_by', {
								user: link.updatedBy,
								date: formatDate(link.updatedAt, i18n.language, true),
								defaultValue: 'Aggiunto a mano da {{user}} il {{date}}',
						  })
						: t('vehicle_association_manual_generic', { defaultValue: 'Aggiunto a mano' })
					: t('vehicle_association_automatic_readonly', {
							defaultValue: 'Trovato dal match automatico, non modificabile',
					  })}
			</StatusNote>
			<div className={styles.actions}>
				{confirming ? (
					<>
						<span>
							{t('vehicle_association_remove_confirm', {
								provider: name,
								defaultValue: 'Rimuovere {{provider}} da questa riga?',
							})}
						</span>
						<Button variant="link" disabled={removing} onClick={() => setConfirming(false)}>
							{t('cancel', { defaultValue: 'Annulla' })}
						</Button>
						<Button variant="danger" disabled={removing} onClick={onRemove}>
							<SpinnerButton
								busy={removing}
								label={t('vehicle_association_remove_confirm_yes', { defaultValue: 'Sì, rimuovi' })}
							/>
						</Button>
					</>
				) : (
					<>
						<Button variant="link" onClick={onClose}>
							{t('close', { defaultValue: 'Chiudi' })}
						</Button>
						{manual && (
							<>
								{row.providers.length > 1 && (
									<Button variant="outline-danger" onClick={() => setConfirming(true)}>
										{t('vehicle_association_remove', { defaultValue: 'Rimuovi' })}
									</Button>
								)}
								<Button variant="secondary" onClick={onReplace}>
									{t('vehicle_association_replace', { defaultValue: 'Sostituisci' })}
								</Button>
							</>
						)}
					</>
				)}
			</div>
		</>
	);
};

type WizardProps = {
	row: CatalogRow;
	provider: CatalogProvider;
	saving: boolean;
	disabled?: boolean;
	extraAction?: ReactNode;
	onCancel: () => void;
	onSave: (ratebookId: string) => void;
};

type Picked = { make?: string; model?: string; version?: string; ratebookId?: string };

const ProviderWizard = ({ row, provider, saving, disabled = false, extraAction, onCancel, onSave }: WizardProps) => {
	const { t } = useTranslation();
	const { noOptions, makeLabel, modelLabel, versionLabel } = useSelectLabels();
	const [picked, setPicked] = useState<Picked>({});
	const all = useCatalogCandidates({ provider });
	const rowMake = all.data ? facetMatch(all.data.facets.makes, row.make) : undefined;
	const make = rowMake ?? picked.make;
	const listing = useCatalogCandidates({ provider, make, model: picked.model }, make != null);
	const rows = useCatalogCandidates(
		{ provider, make, model: picked.model, version: picked.version },
		make != null && picked.version != null
	);
	const error = all.error ?? listing.error ?? rows.error;
	if (error) {
		return (
			<div className="py-2 d-flex align-items-center gap-3">
				<span className="text-danger">{error.message || t('error_loading_data', { defaultValue: 'Errore non specificato durante il caricamento dei dati' })}</span>
				<Button
					size="sm"
					variant="secondary"
					onClick={() => {
						void all.refetch();
						if (make) void listing.refetch();
						if (make && picked.version) void rows.refetch();
					}}
				>
					{t('vehicle_association_retry', { defaultValue: 'Riprova' })}
				</Button>
			</div>
		);
	}
	const name = PROVIDER_LABELS[provider];
	const title = (
		<div className={styles.sectionTitle}>
			{t('vehicle_association_associate_provider', { provider: name, defaultValue: 'Associa {{provider}}' })}
		</div>
	);

	if (!all.data) {
		return (
			<>
				{title}
				<div className="text-center py-3">
					<Spinner animation="border" size="sm" />
				</div>
			</>
		);
	}

	const listingData = listing.isPlaceholderData ? undefined : listing.data;
	const showItems = picked.version != null;
	const loadingItems = showItems && (rows.isPending || rows.isPlaceholderData);
	const items = showItems && !loadingItems ? rows.data?.items ?? [] : [];
	const chosen = items.find((c) => c.ratebookId === picked.ratebookId) ?? null;
	const warning = chosen ? currentRowWarning(chosen, row.versionKey) : 'none';
	const busy = listing.isFetching || rows.isFetching || saving || disabled;
	const modelOptions = toOptions(make ? listingData?.facets.models ?? [] : []);
	const versionOptions: Option<string>[] = (picked.model ? listingData?.facets.versions ?? [] : []).map((version) => {
		const codes = versionManufacturerCodes(listingData?.items ?? [], version);
		return {
			value: version,
			label: version,
			subtitle: codes.join(' · ') || undefined,
			highlight: codes.some((code) => matchesManufacturerCode(code, row)),
		};
	});
	const priceLabel = t('vehicle_association_select_price', { defaultValue: 'Seleziona prezzo' });
	const priceOptions: Option<string>[] = items.map((c) => ({
		value: c.ratebookId,
		label: c.listPrice == null ? '-' : formatCurrency(c.listPrice),
		subtitle: `${t('vehicle_association_manufacturer_code', { defaultValue: 'Cod. costr.' })} ${c.manufacturerCode ?? '-'}`,
		highlight: matchesListPrice(c.listPrice, row.listPrice),
	}));

	return (
		<>
			{title}
			<WizardFields>
				<WizardField label={t('brand', { defaultValue: 'Marca' })} width="calc(100% / 6)">
					{rowMake ? (
						<Form.Control value={rowMake} aria-label={t('brand', { defaultValue: 'Marca' })} readOnly disabled />
					) : (
						<Select<Option<string>>
							options={toOptions(all.data.facets.makes)}
							value={make ? { value: make, label: make } : null}
							isDisabled={busy}
							placeholder={makeLabel}
							aria-label={makeLabel}
							{...portalProps}
							noOptionsMessage={noOptions}
							onChange={(option) => option && setPicked({ make: option.value })}
						/>
					)}
				</WizardField>
				<WizardField label={t('model', { defaultValue: 'Modello' })} width={280}>
					<Select<Option<string>>
						options={modelOptions}
						value={selectedOption(modelOptions, picked.model)}
						isDisabled={busy || !make}
						isLoading={listing.isFetching}
						placeholder={modelLabel}
						aria-label={modelLabel}
						{...portalProps}
						noOptionsMessage={noOptions}
						onChange={(option) => option && setPicked({ make: picked.make, model: option.value })}
					/>
				</WizardField>
				<WizardField label={t('version', { defaultValue: 'Versione' })} width={420}>
					<Select<Option<string>>
						options={versionOptions}
						formatOptionLabel={formatWithSubtitle}
						value={selectedOption(versionOptions, picked.version)}
						isDisabled={busy || !picked.model}
						isLoading={listing.isFetching}
						placeholder={versionLabel}
						aria-label={versionLabel}
						{...portalProps}
						noOptionsMessage={noOptions}
						onChange={(option) =>
							option && setPicked({ make: picked.make, model: picked.model, version: option.value })
						}
					/>
				</WizardField>
				<WizardField label={t('vehicle_association_list_price', { defaultValue: 'Listino netto' })} width={220}>
					<Select<Option<string>>
						options={priceOptions}
						formatOptionLabel={formatWithSubtitle}
						value={findOption(priceOptions, chosen?.ratebookId)}
						isDisabled={busy || !showItems}
						isLoading={loadingItems}
						placeholder={priceLabel}
						aria-label={priceLabel}
						{...portalProps}
						noOptionsMessage={noOptions}
						onChange={(option) => option && setPicked({ ...picked, ratebookId: option.value })}
					/>
				</WizardField>
			</WizardFields>

			{showItems && items.length === 0 && !loadingItems && (
				<div className="text-muted mt-3">
					{t('vehicle_association_no_candidates', { defaultValue: 'Nessuna riga nel listino del noleggiatore' })}
				</div>
			)}

			{showItems && !loadingItems && rows.data && rows.data.total > items.length && (
				<div className="small text-muted mt-2">
					{t('vehicle_association_candidates_truncated', {
						shown: items.length,
						total: rows.data.total,
						defaultValue: 'Mostrate {{shown}} di {{total}} righe: restringi la selezione',
					})}
				</div>
			)}

			{/* {chosen?.currentRow && warning !== 'none' && (
				<Alert variant="warning" className="mt-3 mb-0">
					{t('vehicle_association_moves_from', {
						label: rowLabel(chosen.currentRow),
						defaultValue: 'Verrà tolta da {{label}}.',
					})}
					{warning === 'empties' &&
						` ${t('vehicle_association_row_empties', {
							defaultValue: 'La riga resterà senza noleggiatori e sparirà dalla lista.',
						})}`}
				</Alert>
			)} */}

			<div className={styles.actions}>
				<Button variant="link" disabled={saving || disabled} onClick={onCancel}>
					{t('cancel', { defaultValue: 'Annulla' })}
				</Button>
				{extraAction}
				<Button disabled={busy || !chosen} onClick={() => chosen && onSave(chosen.ratebookId)}>
					<SpinnerButton
						busy={saving}
						label={t('vehicle_association_save_link', { defaultValue: 'Salva associazione' })}
					/>
				</Button>
			</div>
		</>
	);
};

export default ProviderPanel;
